import { isValidElement, type ReactNode } from "react";
import { format, isValid, parseISO } from "date-fns";
import { formatNumberWithComma } from "@/utils/formatNumber";
import { formatJPY } from "@/utils/formatJPY";

/** 値が無い／表示できないときの表示 */
export const EMPTY_DISPLAY = "—";

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);

/**
 * 数値を3桁区切り＋単位で表示する。null・undefined・数値以外・NaN/Infinity は「—」
 * @param value 数値
 * @param unit 単位（例: "人"）
 * @returns 表示文字列
 */
export const formatNumberOrDash = (value: unknown, unit = ""): string =>
  isFiniteNumber(value)
    ? `${formatNumberWithComma(value)}${unit}`
    : EMPTY_DISPLAY;

/**
 * 円を "¥1,234" 形式で表示する。null・undefined・数値以外は「—」
 * @param value 金額（円）
 * @returns 表示文字列
 */
export const formatYenOrDash = (value: unknown): string =>
  isFiniteNumber(value) ? formatJPY(value) : EMPTY_DISPLAY;

/**
 * 小数の比率（0.1393）をパーセント（13.93%）で表示する。null・数値以外は「—」
 * @param value 小数の比率
 * @param fractionDigits 小数点以下の桁数（固定）
 * @returns 表示文字列
 */
export const formatRatioAsPercent = (
  value: unknown,
  fractionDigits = 2
): string =>
  isFiniteNumber(value)
    ? `${(value * 100).toFixed(fractionDigits)}%`
    : EMPTY_DISPLAY;

/**
 * 円を億/万単位に丸めて表示する（負数は絶対値で単位を決め、符号を保つ）
 * @param value 金額（円）
 * @param unit 末尾の単位（グラフ軸では "" を渡す）
 * @returns 表示文字列。値が無ければ「—」
 */
export const formatYenCompact = (value: unknown, unit = "円"): string => {
  if (!isFiniteNumber(value)) return EMPTY_DISPLAY;
  const abs = Math.abs(value);
  const sign = value < 0 ? "-" : "";
  if (abs >= 1_0000_0000)
    return `${sign}${(abs / 1_0000_0000).toLocaleString("ja-JP", {
      maximumFractionDigits: 1,
    })}億${unit}`;
  if (abs >= 1_0000)
    return `${sign}${Math.round(abs / 1_0000).toLocaleString(
      "ja-JP"
    )}万${unit}`;
  return `${value.toLocaleString("ja-JP")}${unit}`;
};

const parseDate = (value: unknown): Date | null => {
  if (typeof value !== "string" || value === "") return null;
  const date = parseISO(value);
  return isValid(date) ? date : null;
};

/**
 * ISO日時を "yyyy/MM/dd HH:mm" で表示する。null・undefined・不正値は「—」
 * @param value ISO 8601 の日時文字列
 * @returns 表示文字列
 */
export const formatDateTime = (value: unknown): string => {
  const date = parseDate(value);
  return date ? format(date, "yyyy/MM/dd HH:mm") : EMPTY_DISPLAY;
};

/**
 * ISO日付を "yyyy/MM/dd" で表示する。null・undefined・不正値は「—」
 * @param value ISO 8601 の日付文字列（YYYY-MM-DD）
 * @returns 表示文字列
 */
export const formatDate = (value: unknown): string => {
  const date = parseDate(value);
  return date ? format(date, "yyyy/MM/dd") : EMPTY_DISPLAY;
};

/**
 * 生年月日から西暦の生年を取り出す（"1944年9月30日" / "1944-09-30" → "1944"）。
 * 西暦で始まらない表記（和暦等）は元の値をそのまま返す
 * @param value 生年月日
 * @returns 表示文字列
 */
export const formatBirthYear = (value: string | null | undefined): string => {
  if (!value) return EMPTY_DISPLAY;
  const match = /^(\d{4})(?:年|-|\/)/.exec(value.trim());
  return match ? match[1] : value;
};

/**
 * 表セルの値を描画できる形にする。外部データ由来の値にオブジェクト/配列が紛れても落とさない。
 * React要素・文字列・数値はそのまま、null/undefined/空文字は「—」、真偽値は はい/いいえ
 * @param value セルの値
 * @returns 描画する値
 */
export const toCellText = (value: unknown): ReactNode => {
  if (value === null || value === undefined || value === "")
    return EMPTY_DISPLAY;
  if (isValidElement(value)) return value;
  if (typeof value === "string" || typeof value === "number") return value;
  if (typeof value === "boolean") return value ? "はい" : "いいえ";
  if (Array.isArray(value)) {
    const joined = value
      .map((v) => toCellText(v))
      .filter((v) => v !== EMPTY_DISPLAY);
    return joined.length ? joined.join(" / ") : EMPTY_DISPLAY;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return EMPTY_DISPLAY;
  }
};
